import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../Common/Navbar";
import "./SimpleCpanel.css";

import {
  connectRealtimeSocket,
  emitHostingPanelRefreshRequest,
} from "../ServerPackage/CompleteCPanel/utils/realtimeSocket";

const normalizeBase = (value) => String(value || "").trim().replace(/\/+$/, "");

const API_BASE = normalizeBase(
  process.env.REACT_APP_API_BASE || "http://localhost:5000"
).replace(/\/api$/, "");

const authTokenKeys = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
];

const getToken = () => {
  for (const key of authTokenKeys) {
    const value = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (value && String(value).trim()) return String(value).trim();
  }
  return "";
};

const defaultDeployLogs = [
  "Waiting for project upload...",
  "Upload your website ZIP or folder to start deployment.",
];

const lockedDeployLogs = [
  "Hosting panel locked.",
  "Please purchase or activate a hosting plan to deploy your website.",
];

const allowedFilesText =
  "Supported: React build folder, Vite dist folder, HTML/CSS/JS static website, or ZIP containing index.html";

const statusLabel = {
  idle: "Waiting Upload",
  locked: "Plan Required",
  ready: "Ready to Deploy",
  uploading: "Uploading",
  processing: "Processing",
  deploying: "Deploying",
  live: "Preview Live",
  failed: "Failed",
  deleted: "Deleted",
};

const domainStatusLabel = {
  pending_dns: "DNS Propagating",
  connecting: "Connecting",
  active: "Live Connected",
  failed: "Failed",
  disabled: "Disabled",
};

const getStatusLabel = (status) => statusLabel[status] || "Waiting Upload";

const getDomainStatusLabel = (status) =>
  domainStatusLabel[status] || status || "Connecting";

const getFileSizeLabel = (bytes = 0) => {
  const size = Number(bytes || 0);

  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;

  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const slugifyProjectName = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const normalizeUrl = (value = "") => {
  const clean = String(value || "").trim();
  if (!clean) return "";
  if (/^https?:\/\//i.test(clean)) return clean;
  return `https://${clean}`;
};

const normalizeDomainName = (value = "") => {
  return String(value || "")
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/\/+$/, "")
    .toLowerCase();
};

const buildLiveUrlFromDomain = (domain = "") => {
  const clean = normalizeDomainName(domain);
  return clean ? `https://${clean}` : "";
};

const getProjectPreviewUrl = (project = {}) => {
  return normalizeUrl(
    project.previewUrl ||
      project.preview_url ||
      project.defaultLiveUrl ||
      project.default_live_url ||
      project.temporaryUrl ||
      project.temporary_url ||
      project.projectUrl ||
      project.project_url ||
      project.liveUrl ||
      project.live_url ||
      ""
  );
};

const getMappedDomain = (project = {}) => {
  return normalizeDomainName(
    project.mappedDomain ||
      project.mapped_domain ||
      project.customDomain ||
      project.custom_domain ||
      project.domainName ||
      project.domain_name ||
      ""
  );
};

const getDomainStatus = (project = {}) => {
  return (
    project.domainMappingStatus ||
    project.domain_mapping_status ||
    project.dnsStatus ||
    project.dns_status ||
    "connecting"
  );
};

const getRawCustomUrl = (project = {}) => {
  return normalizeUrl(
    project.customLiveUrl ||
      project.custom_live_url ||
      project.customDomainUrl ||
      project.custom_domain_url ||
      project.displayUrl ||
      project.display_url ||
      ""
  );
};

const isDomainActive = (project = {}) => {
  return getDomainStatus(project) === "active";
};

const getSafePreviewUrl = (project = {}) => {
  return getProjectPreviewUrl(project);
};

const getSafeLiveUrl = (project = {}) => {
  const customUrl = getRawCustomUrl(project);
  const mappedDomain = getMappedDomain(project);

  if (isDomainActive(project)) {
    return customUrl || buildLiveUrlFromDomain(mappedDomain);
  }

  return "";
};

const getDisplayUrl = (project = {}) => {
  return getSafeLiveUrl(project) || getSafePreviewUrl(project);
};

const getSslLabel = (value = "") => {
  const clean = String(value || "").toLowerCase();

  if (clean === "active") return "SSL Active";
  if (clean === "issued") return "SSL Issued";
  if (clean === "pending") return "SSL Pending";
  if (clean === "failed") return "SSL Needs Attention";

  return "SSL Required / Auto Activation";
};

const normalizeProject = (project = {}) => {
  const normalized = {
    id: project.id || project.projectId || project.project_id,
    userId: project.userId || project.user_id,
    projectName:
      project.projectName || project.project_name || "Website Project",
    projectSlug: project.projectSlug || project.project_slug || "",
    status: project.status || "idle",

    liveUrl: normalizeUrl(project.liveUrl || project.live_url || ""),
    displayUrl: normalizeUrl(project.displayUrl || project.display_url || ""),
    defaultLiveUrl: normalizeUrl(
      project.defaultLiveUrl || project.default_live_url || ""
    ),
    previewUrl: normalizeUrl(project.previewUrl || project.preview_url || ""),
    customLiveUrl: normalizeUrl(
      project.customLiveUrl || project.custom_live_url || ""
    ),
    customDomainUrl: normalizeUrl(
      project.customDomainUrl || project.custom_domain_url || ""
    ),
    mappedDomain: getMappedDomain(project),
    domainMappingStatus: getDomainStatus(project),

    sslRequired: true,
    needSsl: true,
    emailRequired: false,
    needEmail: false,
    sslStatus: project.sslStatus || project.ssl_status || "required",

    totalFiles: Number(project.totalFiles || project.total_files || 0),
    totalSize: Number(project.totalSize || project.total_size || 0),
    totalSizeLabel:
      project.totalSizeLabel ||
      project.total_size_label ||
      getFileSizeLabel(project.totalSize || project.total_size || 0),
    errorMessage: project.errorMessage || project.error_message || "",
    createdAt: project.createdAt || project.created_at || "",
    updatedAt: project.updatedAt || project.updated_at || "",
  };

  normalized.previewUrl = getSafePreviewUrl(normalized);
  normalized.liveUrl = getSafeLiveUrl(normalized);
  normalized.safeLiveUrl = getDisplayUrl(normalized);
  normalized.domainActive = isDomainActive(normalized);

  return normalized;
};

const normalizeHostingEntitlement = (data = {}) => {
  const item = data?.data || data?.plan || data || {};

  const status = String(item.status || "").toLowerCase();
  const setupStatus = String(
    item.setupStatus || item.setup_status || ""
  ).toLowerCase();

  const isActive =
    status === "active" ||
    item.active === true ||
    item.isActive === true ||
    item.hasHostingAccess === true;

  return {
    id: item.id || "",
    planKey: item.planKey || item.plan_key || "",
    planName: item.planName || item.plan_name || item.plan || "Hosting Plan",
    status: status || "pending",
    setupStatus: setupStatus || "pending_setup",
    hasAccess: Boolean(isActive),
  };
};

function SimpleCpanel() {
  const navigate = useNavigate();

  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const activeProjectIdRef = useRef(null);

  const [projectName, setProjectName] = useState("my-website");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [deployStatus, setDeployStatus] = useState("idle");

  const [liveUrl, setLiveUrl] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [mappedDomain, setMappedDomain] = useState("");
  const [domainMappingStatus, setDomainMappingStatus] = useState("");
  const [sslStatus, setSslStatus] = useState("required");

  const [logs, setLogs] = useState(defaultDeployLogs);
  const [projects, setProjects] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState(null);

  const [hostingEntitlement, setHostingEntitlement] = useState(null);
  const [checkingAccess, setCheckingAccess] = useState(true);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [realtimeStatus, setRealtimeStatus] = useState("connecting");

  useEffect(() => {
    activeProjectIdRef.current = activeProjectId;
  }, [activeProjectId]);

  const hasHostingAccess = Boolean(hostingEntitlement?.hasAccess);

  const currentActivationLabel = hasHostingAccess
    ? `${hostingEntitlement?.planName || "Hosting Plan"} • ${
        hostingEntitlement?.status || "active"
      }`
    : "No active hosting plan";

  const currentSslLabel = getSslLabel(sslStatus);

  const totalFiles = selectedFiles.length;

  const totalSize = useMemo(() => {
    const size = selectedFiles.reduce(
      (sum, file) => sum + Number(file.size || 0),
      0
    );

    return getFileSizeLabel(size);
  }, [selectedFiles]);

  const selectedRelativePaths = useMemo(() => {
    return selectedFiles.map((file) => file.webkitRelativePath || file.name);
  }, [selectedFiles]);

  const latestProject = useMemo(() => {
    if (!projects.length) return null;
    return projects[0];
  }, [projects]);

  const latestProjectLiveUrl = useMemo(() => {
    if (!latestProject) return "";
    return getSafeLiveUrl(latestProject);
  }, [latestProject]);

  const latestProjectPreviewUrl = useMemo(() => {
    if (!latestProject) return "";
    return getSafePreviewUrl(latestProject);
  }, [latestProject]);

  const latestProjectDisplayUrl = useMemo(() => {
    if (!latestProject) return "";
    return getDisplayUrl(latestProject);
  }, [latestProject]);

  const activeDomainConnected = domainMappingStatus === "active";

  const authFetch = useCallback(async (endpoint, options = {}) => {
    const token = getToken();

    if (!token) {
      throw new Error("Please login first to use hosting panel.");
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      cache: "no-store",
      headers: {
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });

    const data = await response.json().catch(() => ({}));

    if (response.status === 401 || response.status === 403) {
      throw new Error("Session expired. Please login again.");
    }

    if (response.status === 429) {
      throw new Error("Too many requests. Please wait a moment and try again.");
    }

    if (!response.ok || data?.success === false) {
      throw new Error(data?.message || "Request failed.");
    }

    return data;
  }, []);

  const loadHostingAccess = useCallback(async () => {
    try {
      setCheckingAccess(true);
      const data = await authFetch("/api/hosting/plan");
      const entitlement = normalizeHostingEntitlement(data);

      setHostingEntitlement(entitlement);

      if (!entitlement.hasAccess) {
        setDeployStatus("locked");
        setLogs(lockedDeployLogs);
        setMessage("");
        setError(
          "Hosting panel access requires an active paid hosting plan. Please activate a plan first."
        );
      } else {
        setError("");
        setLogs(defaultDeployLogs);
        setDeployStatus((prev) => (prev === "locked" ? "idle" : prev));
      }
    } catch (err) {
      setHostingEntitlement({ hasAccess: false });
      setDeployStatus("locked");
      setLogs(lockedDeployLogs);
      setError(err.message || "Unable to verify hosting access.");
    } finally {
      setCheckingAccess(false);
    }
  }, [authFetch]);

  const loadProjects = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (!hasHostingAccess) return;
        if (!silent) setLoadingProjects(true);

        const data = await authFetch("/api/hosting/panel/projects");
        const rows = Array.isArray(data?.data) ? data.data : [];
        const normalizedProjects = rows.map(normalizeProject);

        setProjects(normalizedProjects);

        if (normalizedProjects.length && !liveUrl) {
          const latest = normalizedProjects[0];

          setLiveUrl(getSafeLiveUrl(latest));
          setPreviewUrl(getSafePreviewUrl(latest));
          setMappedDomain(getMappedDomain(latest));
          setDomainMappingStatus(getDomainStatus(latest));
          setSslStatus(latest.sslStatus || "required");

          if (latest.status) {
            setDeployStatus(latest.status);
          }
        }
      } catch (err) {
        if (!silent) {
          setError(err.message || "Unable to load hosting projects.");
        }
      } finally {
        setLoadingProjects(false);
      }
    },
    [authFetch, hasHostingAccess, liveUrl]
  );

  useEffect(() => {
    loadHostingAccess();
  }, [loadHostingAccess]);

  useEffect(() => {
    if (!hasHostingAccess) return;

    loadProjects({ silent: true });
    emitHostingPanelRefreshRequest();
  }, [hasHostingAccess, loadProjects]);

  useEffect(() => {
    const socket = connectRealtimeSocket();

    if (!socket) {
      setRealtimeStatus("offline");
      return undefined;
    }

    const handleConnected = () => {
      setRealtimeStatus("connected");
    };

    const handleDisconnected = () => {
      setRealtimeStatus("offline");
    };

    const handleHostingStatus = (payload = {}) => {
      if (payload?.type === "HOSTING_PLAN_SELECTED") {
        setHostingEntitlement({
          id: payload.serviceId || "",
          planKey: payload.planKey || "",
          planName: payload.planName || "Hosting Plan",
          status: "active",
          setupStatus: payload.setupStatus || "pending_setup",
          hasAccess: true,
        });

        setDeployStatus("idle");
        setLogs(defaultDeployLogs);
        setError("");
        setSslStatus(payload.sslStatus || "required");
        setMessage(
          payload.message ||
            "Hosting plan activated successfully. You can now deploy your website."
        );
      }

      if (payload?.type === "HOSTING_SETUP_UPDATED") {
        setHostingEntitlement((prev) => ({
          ...(prev || {}),
          hasAccess: true,
          status: payload.status || "active",
          setupStatus: payload.setupStatus || "completed",
        }));

        if (payload.liveUrl || payload.customDomainUrl) {
          setLiveUrl(normalizeUrl(payload.liveUrl || payload.customDomainUrl));
        }

        if (payload.previewUrl || payload.defaultLiveUrl) {
          setPreviewUrl(
            normalizeUrl(payload.previewUrl || payload.defaultLiveUrl)
          );
        }

        if (payload.customDomain || payload.mappedDomain) {
          setMappedDomain(
            normalizeDomainName(payload.customDomain || payload.mappedDomain)
          );
        }

        setDomainMappingStatus(payload.dnsStatus || "active");
        setSslStatus(payload.sslStatus || "required");

        setMessage(
          payload.message ||
            "Hosting setup updated. Preview URL, Live URL and SSL status are refreshed."
        );

        loadProjects({ silent: true });
      }
    };

    const handleProjectCreated = (payload = {}) => {
      if (!hasHostingAccess) return;

      if (payload.projectId) {
        setActiveProjectId(payload.projectId);
      }

      setDeployStatus(payload.status || "uploading");
      setSslStatus(payload.sslStatus || "required");

      setLogs((prev) => [
        ...prev,
        `Project created: ${payload.projectName || "Website Project"}`,
      ]);

      loadProjects({ silent: true });
    };

    const handleProjectUpdate = (payload = {}) => {
      if (!hasHostingAccess) return;

      const payloadProjectId = payload.projectId || payload.id || null;
      const currentActiveProjectId = activeProjectIdRef.current;

      if (payloadProjectId && !currentActiveProjectId) {
        setActiveProjectId(payloadProjectId);
      }

      if (
        currentActiveProjectId &&
        payloadProjectId &&
        Number(payloadProjectId) !== Number(currentActiveProjectId)
      ) {
        return;
      }

      const normalizedPayload = normalizeProject(payload);
      const userLiveUrl = getSafeLiveUrl(normalizedPayload);
      const userPreviewUrl = getSafePreviewUrl(normalizedPayload);
      const userMappedDomain = getMappedDomain(normalizedPayload);
      const userDomainStatus = getDomainStatus(normalizedPayload);

      if (payload.status) {
        setDeployStatus(payload.status);
      }

      if (userLiveUrl) {
        setLiveUrl(userLiveUrl);
      }

      if (userPreviewUrl) {
        setPreviewUrl(userPreviewUrl);
      }

      if (userMappedDomain) {
        setMappedDomain(userMappedDomain);
      }

      if (userDomainStatus) {
        setDomainMappingStatus(userDomainStatus);
      }

      setSslStatus(normalizedPayload.sslStatus || "required");

      if (payload.errorMessage) {
        setError(payload.errorMessage);
      }

      if (userDomainStatus === "active" && userLiveUrl) {
        setMessage(
          "Website live URL is connected successfully. SSL is active/auto-managed."
        );
      } else if (userPreviewUrl) {
        setMessage(
          "Preview URL is live. Custom domain DNS/SSL activation is in progress."
        );
      }

      loadProjects({ silent: true });
    };

    const handleProjectDeleted = (payload = {}) => {
      if (!hasHostingAccess) return;

      const currentActiveProjectId = activeProjectIdRef.current;

      if (
        currentActiveProjectId &&
        payload.projectId &&
        Number(payload.projectId) === Number(currentActiveProjectId)
      ) {
        setActiveProjectId(null);
        setLiveUrl("");
        setPreviewUrl("");
        setMappedDomain("");
        setDomainMappingStatus("");
        setSslStatus("required");
        setDeployStatus("idle");
        setLogs(defaultDeployLogs);
      }

      loadProjects({ silent: true });
    };

    const handleLog = (payload = {}) => {
      if (!hasHostingAccess) return;

      const payloadProjectId = payload.projectId || null;
      const currentActiveProjectId = activeProjectIdRef.current;

      if (
        currentActiveProjectId &&
        payloadProjectId &&
        Number(payloadProjectId) !== Number(currentActiveProjectId)
      ) {
        return;
      }

      const text = payload?.log?.text || "";

      if (!text) return;

      setLogs((prev) => [...prev, text]);
    };

    socket.on("connect", handleConnected);
    socket.on("disconnect", handleDisconnected);
    socket.on("socket:connected", handleConnected);

    socket.on("hosting:status", handleHostingStatus);
    socket.on("hosting_panel:project_created", handleProjectCreated);
    socket.on("hosting_panel:project_update", handleProjectUpdate);
    socket.on("hosting_panel:project_deleted", handleProjectDeleted);
    socket.on("hosting_panel:log", handleLog);

    if (socket.connected) {
      handleConnected();
    }

    return () => {
      socket.off("connect", handleConnected);
      socket.off("disconnect", handleDisconnected);
      socket.off("socket:connected", handleConnected);

      socket.off("hosting:status", handleHostingStatus);
      socket.off("hosting_panel:project_created", handleProjectCreated);
      socket.off("hosting_panel:project_update", handleProjectUpdate);
      socket.off("hosting_panel:project_deleted", handleProjectDeleted);
      socket.off("hosting_panel:log", handleLog);
    };
  }, [hasHostingAccess, loadProjects]);

  const resetSelectedProjectUi = () => {
    setLiveUrl("");
    setPreviewUrl("");
    setMappedDomain("");
    setDomainMappingStatus("");
    setSslStatus("required");
    setActiveProjectId(null);
    setMessage("");
    setError("");
  };

  const ensureDeployAccess = () => {
    if (checkingAccess) {
      setError("Checking hosting access. Please wait.");
      return false;
    }

    if (!hasHostingAccess) {
      setDeployStatus("locked");
      setLogs(lockedDeployLogs);
      setError(
        "Hosting panel is locked. Please activate a paid hosting plan before uploading or deploying a website."
      );
      return false;
    }

    return true;
  };

  const handleFileSelect = (event) => {
    if (!ensureDeployAccess()) {
      event.target.value = "";
      return;
    }

    const files = Array.from(event.target.files || []);

    setSelectedFiles(files);
    setDeployStatus(files.length ? "ready" : "idle");
    resetSelectedProjectUi();

    if (files.length) {
      setLogs([
        `${files.length} file(s) selected successfully.`,
        "Ready to upload and deploy.",
      ]);
    } else {
      setLogs(defaultDeployLogs);
    }
  };

  const handleFolderSelect = (event) => {
    if (!ensureDeployAccess()) {
      event.target.value = "";
      return;
    }

    const files = Array.from(event.target.files || []);

    setSelectedFiles(files);
    setDeployStatus(files.length ? "ready" : "idle");
    resetSelectedProjectUi();

    if (files.length) {
      setLogs([
        `${files.length} folder file(s) selected successfully.`,
        "Ready to upload and deploy.",
      ]);
    } else {
      setLogs(defaultDeployLogs);
    }
  };

  const openFilePicker = () => {
    if (!ensureDeployAccess()) return;
    fileInputRef.current?.click();
  };

  const openFolderPicker = () => {
    if (!ensureDeployAccess()) return;
    folderInputRef.current?.click();
  };

  const deployProject = async () => {
    if (!ensureDeployAccess()) return;

    if (!selectedFiles.length) {
      setError("Please upload your website ZIP, files, or folder first.");
      return;
    }

    const token = getToken();

    if (!token) {
      setError("Please login first to deploy your website.");
      return;
    }

    try {
      setError("");
      setMessage("");
      setLiveUrl("");
      setPreviewUrl("");
      setMappedDomain("");
      setDomainMappingStatus("");
      setSslStatus("required");
      setActiveProjectId(null);
      setDeployStatus("deploying");

      setLogs([
        "Deployment request started...",
        "Uploading project to server...",
        `${currentSslLabel}.`,
        "Email setup is not required for this deployment.",
      ]);

      const safeProjectName = slugifyProjectName(projectName) || "my-website";

      const formData = new FormData();

      formData.append("projectName", safeProjectName);
      formData.append("relativePaths", JSON.stringify(selectedRelativePaths));
      formData.append("sslRequired", "true");
      formData.append("needSsl", "true");
      formData.append("emailRequired", "false");
      formData.append("needEmail", "false");

      selectedFiles.forEach((file) => {
        formData.append("files", file);
      });

      const response = await fetch(`${API_BASE}/api/hosting/panel/deploy`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json().catch(() => ({}));

      if (response.status === 402 || response.status === 403) {
        setHostingEntitlement((prev) => ({
          ...(prev || {}),
          hasAccess: false,
        }));
        setDeployStatus("locked");
        throw new Error(
          data?.message ||
            "Active paid hosting plan is required to deploy websites."
        );
      }

      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || "Deployment failed.");
      }

      const project = normalizeProject(data?.data || {});

      if (project.id) {
        setActiveProjectId(project.id);
      }

      const userLiveUrl = getSafeLiveUrl(project);
      const userPreviewUrl = getSafePreviewUrl(project);
      const displayUrl = getDisplayUrl(project);
      const userMappedDomain = getMappedDomain(project);
      const userDomainStatus = getDomainStatus(project);
      const projectSslStatus = project.sslStatus || "required";

      setDeployStatus(project.status || "live");
      setLiveUrl(userLiveUrl);
      setPreviewUrl(userPreviewUrl);
      setMappedDomain(userMappedDomain);
      setDomainMappingStatus(userDomainStatus);
      setSslStatus(projectSslStatus);

      if (userDomainStatus === "active" && userLiveUrl) {
        setMessage(
          "Website deployed successfully. Live URL is connected and SSL is auto-managed."
        );
      } else if (userPreviewUrl) {
        setMessage(
          "Website deployed successfully. Preview URL is live. Custom domain DNS/SSL is connecting automatically."
        );
      } else {
        setMessage(data?.message || "Website deployed successfully.");
      }

      setLogs((prev) =>
        [
          ...prev,
          "Deployment completed successfully.",
          userPreviewUrl ? `Preview URL: ${userPreviewUrl}` : "",
          userLiveUrl ? `Live URL: ${userLiveUrl}` : "",
          userMappedDomain ? `Custom domain: ${userMappedDomain}` : "",
          `Domain status: ${getDomainStatusLabel(userDomainStatus)}`,
          `SSL: ${getSslLabel(projectSslStatus)}`,
          "Email: Not Required",
        ].filter(Boolean)
      );

      if (displayUrl) {
        setLogs((prev) => [...prev, `Current user-facing URL: ${displayUrl}`]);
      }

      await loadProjects({ silent: true });
    } catch (err) {
      setDeployStatus(hasHostingAccess ? "ready" : "locked");
      setError(err.message || "Unable to deploy project.");
      setLogs((prev) => [
        ...prev,
        `Error: ${err.message || "Deployment failed."}`,
      ]);
    }
  };

  const deleteProject = async (projectId) => {
    if (!projectId || !ensureDeployAccess()) return;

    try {
      setError("");
      setMessage("");

      await authFetch(`/api/hosting/panel/projects/${projectId}`, {
        method: "DELETE",
      });

      setProjects((prev) =>
        prev.filter((project) => Number(project.id) !== Number(projectId))
      );

      if (Number(activeProjectIdRef.current) === Number(projectId)) {
        setActiveProjectId(null);
        setLiveUrl("");
        setPreviewUrl("");
        setMappedDomain("");
        setDomainMappingStatus("");
        setSslStatus("required");
        setDeployStatus("idle");
        setLogs(defaultDeployLogs);
      }

      setMessage("Project deleted successfully.");
    } catch (err) {
      setError(err.message || "Unable to delete project.");
    }
  };

  const resetProject = () => {
    setSelectedFiles([]);
    setDeployStatus(hasHostingAccess ? "idle" : "locked");
    setLiveUrl("");
    setPreviewUrl("");
    setMappedDomain("");
    setDomainMappingStatus("");
    setSslStatus("required");
    setActiveProjectId(null);
    setLogs(hasHostingAccess ? defaultDeployLogs : lockedDeployLogs);
    setMessage("");
    setError("");

    if (fileInputRef.current) fileInputRef.current.value = "";
    if (folderInputRef.current) folderInputRef.current.value = "";
  };

  const copyLiveUrl = async (url) => {
    if (!url) return;

    try {
      await navigator.clipboard?.writeText(url);
      setMessage("URL copied successfully.");
    } catch {
      setMessage("URL is ready. You can copy it manually.");
    }
  };

  const openWebsite = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const goToHostingPlans = () => {
    navigate("/hosting");
  };

  const renderAccessLock = () => {
    if (checkingAccess) {
      return (
        <div className="simple-alert success">
          Checking your hosting plan access...
        </div>
      );
    }

    if (hasHostingAccess) return null;

    return (
      <div className="simple-alert error">
        Hosting panel locked. Please activate a paid hosting plan to upload,
        deploy and manage live websites.
        <button type="button" onClick={goToHostingPlans}>
          View Hosting Plans
        </button>
      </div>
    );
  };

  const renderLiveBox = ({ title, live, preview, domain, domainStatus }) => {
    const domainActive = domainStatus === "active";
    const displayUrl = domainActive && live ? live : preview || live;

    return (
      <div className="simple-live-box">
        <p>{title}</p>

        {preview ? (
          <>
            <strong>Preview URL</strong>
            <a href={preview} target="_blank" rel="noreferrer">
              {preview}
            </a>
          </>
        ) : null}

        {domain ? (
          <>
            <strong>Live URL</strong>
            {domainActive && live ? (
              <a href={live} target="_blank" rel="noreferrer">
                {live}
              </a>
            ) : (
              <small className="simple-preview-url">
                https://{domain} — DNS/SSL connecting automatically
              </small>
            )}

            <small className="simple-preview-url">
              Custom Domain: {domain} •{" "}
              {getDomainStatusLabel(domainStatus || "pending_dns")}
            </small>
          </>
        ) : null}

        <small className="simple-preview-url">SSL: {currentSslLabel}</small>

        <small className="simple-preview-url">Email: Not Required</small>

        {domain && !domainActive ? (
          <small className="simple-preview-url">
            Preview URL is already live. Custom domain will switch to Live URL
            automatically after DNS and SSL activation.
          </small>
        ) : null}

        <div className="simple-live-actions">
          {preview ? (
            <button type="button" onClick={() => openWebsite(preview)}>
              Open Preview URL
            </button>
          ) : null}

          {domainActive && live ? (
            <button type="button" onClick={() => openWebsite(live)}>
              Open Live URL
            </button>
          ) : null}

          {displayUrl ? (
            <button type="button" onClick={() => copyLiveUrl(displayUrl)}>
              Copy URL
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <>
      <Navbar />

      <main className="simple-cpanel-page">
        <section className="simple-cpanel-hero">
          <div>
            <span className="simple-cpanel-badge">
              DoHostingPay Control Panel
            </span>

            <h1>Simple Hosting Panel</h1>

            <p>
              Upload your website code, deploy it automatically, preview it
              instantly, and connect your live custom domain with SSL.
            </p>

            <div className="simple-cpanel-status-row">
              <span className={`simple-status-pill ${deployStatus}`}>
                {getStatusLabel(deployStatus)}
              </span>

              <span className="simple-realtime-pill">
                {realtimeStatus === "connected"
                  ? "🟢 Realtime connected"
                  : realtimeStatus === "connecting"
                  ? "🟡 Connecting realtime"
                  : "⚪ Realtime offline"}
              </span>

              <span className="simple-realtime-pill">
                {hasHostingAccess
                  ? `✅ Current Activation: ${currentActivationLabel}`
                  : "🔒 Plan Required"}
              </span>

              <span className="simple-realtime-pill">
                🔐 {currentSslLabel}
              </span>
            </div>
          </div>

          <div className="simple-cpanel-hero-actions">
            <button type="button" onClick={() => navigate("/dashboard")}>
              User Dashboard
            </button>

            {!hasHostingAccess ? (
              <button
                type="button"
                className="primary"
                onClick={goToHostingPlans}
              >
                Activate Hosting
              </button>
            ) : (
              <button type="button" className="primary" onClick={openFilePicker}>
                Upload Code
              </button>
            )}
          </div>
        </section>

        {renderAccessLock()}

        {message ? <div className="simple-alert success">{message}</div> : null}
        {error ? <div className="simple-alert error">{error}</div> : null}

        <section className="simple-cpanel-grid">
          <div
            className={`simple-card upload-card ${
              !hasHostingAccess ? "locked" : ""
            }`}
          >
            <span className="simple-card-badge">Upload Project</span>

            <h2>Upload files or folder</h2>

            <p>{allowedFilesText}</p>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".zip,.html,.htm,.css,.js,.mjs,.json,.txt,.xml,.svg,.png,.jpg,.jpeg,.webp,.gif,.ico,.woff,.woff2,.ttf,.eot,.map,.webmanifest"
              onChange={handleFileSelect}
              hidden
            />

            <input
              ref={folderInputRef}
              type="file"
              multiple
              webkitdirectory=""
              onChange={handleFolderSelect}
              hidden
            />

            <button
              type="button"
              className="simple-upload-box"
              onClick={openFilePicker}
              disabled={!hasHostingAccess || checkingAccess}
            >
              <div className="upload-icon">{hasHostingAccess ? "📦" : "🔒"}</div>
              <h3>
                {hasHostingAccess
                  ? "Drop your code here"
                  : "Hosting plan required"}
              </h3>
              <p>
                {hasHostingAccess
                  ? "ZIP, HTML/CSS/JS, React build folder, Vite dist folder or static website files."
                  : "Activate a paid hosting plan to upload and deploy websites."}
              </p>
            </button>

            <div className="simple-upload-actions">
              <button
                type="button"
                onClick={openFilePicker}
                disabled={!hasHostingAccess || checkingAccess}
              >
                Select Files / ZIP
              </button>

              <button
                type="button"
                onClick={openFolderPicker}
                disabled={!hasHostingAccess || checkingAccess}
              >
                Select Folder
              </button>
            </div>
          </div>

          <div
            className={`simple-card deploy-card ${
              !hasHostingAccess ? "locked" : ""
            }`}
          >
            <span className="simple-card-badge">Deployment</span>

            <h2>Deploy website</h2>

            <label className="simple-label">
              Project name
              <input
                type="text"
                value={projectName}
                disabled={!hasHostingAccess || checkingAccess}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="my-website"
              />
            </label>

            <div className="simple-stats">
              <div>
                <span>Total Files</span>
                <strong>{totalFiles}</strong>
              </div>

              <div>
                <span>Total Size</span>
                <strong>{totalSize}</strong>
              </div>

              <div>
                <span>Status</span>
                <strong>{getStatusLabel(deployStatus)}</strong>
              </div>
            </div>

            <button
              type="button"
              className="simple-deploy-btn"
              onClick={deployProject}
              disabled={
                !hasHostingAccess ||
                checkingAccess ||
                deployStatus === "deploying" ||
                deployStatus === "uploading" ||
                deployStatus === "processing"
              }
            >
              {!hasHostingAccess
                ? "Activate Plan First"
                : deployStatus === "deploying" ||
                  deployStatus === "uploading" ||
                  deployStatus === "processing"
                ? "Deploying..."
                : "Upload & Deploy"}
            </button>

            <button
              type="button"
              className="simple-reset-btn"
              onClick={resetProject}
            >
              Reset Project
            </button>
          </div>
        </section>

        <section className="simple-cpanel-grid second">
          <div className="simple-card files-card">
            <div className="simple-card-head">
              <div>
                <span className="simple-card-badge">File Manager</span>
                <h2>Project files</h2>
              </div>

              <span>{totalFiles} files</span>
            </div>

            {selectedFiles.length === 0 ? (
              <div className="simple-empty">
                {hasHostingAccess
                  ? "No files uploaded yet."
                  : "Activate hosting plan to upload project files."}
              </div>
            ) : (
              <div className="simple-file-list">
                {selectedFiles.slice(0, 10).map((file, index) => (
                  <div className="simple-file-row" key={`${file.name}-${index}`}>
                    <span>📄</span>

                    <div>
                      <strong>{file.webkitRelativePath || file.name}</strong>
                      <small>
                        {Math.max(file.size / 1024, 1).toFixed(1)} KB
                      </small>
                    </div>
                  </div>
                ))}

                {selectedFiles.length > 10 ? (
                  <div className="simple-more-files">
                    + {selectedFiles.length - 10} more files selected
                  </div>
                ) : null}
              </div>
            )}
          </div>

          <div className="simple-card preview-card">
            <div className="simple-card-head">
              <div>
                <span className="simple-card-badge">Website URLs</span>
                <h2>Preview & Live URL</h2>
              </div>

              <span className={`simple-mini-status ${deployStatus}`}>
                {getStatusLabel(deployStatus)}
              </span>
            </div>

            {previewUrl || liveUrl ? (
              renderLiveBox({
                title: activeDomainConnected
                  ? "Your live website is ready:"
                  : "Your preview website is live:",
                live: liveUrl,
                preview: previewUrl,
                domain: mappedDomain,
                domainStatus: domainMappingStatus,
              })
            ) : latestProjectDisplayUrl ? (
              renderLiveBox({
                title:
                  latestProject?.domainMappingStatus === "active"
                    ? "Latest live website:"
                    : "Latest preview website:",
                live: latestProjectLiveUrl,
                preview: latestProjectPreviewUrl,
                domain: latestProject?.mappedDomain,
                domainStatus: latestProject?.domainMappingStatus,
              })
            ) : (
              <div className="simple-empty">
                {hasHostingAccess
                  ? "Preview URL and Live URL will appear here after deployment."
                  : "Activate a hosting plan to deploy and get website URLs."}
              </div>
            )}
          </div>
        </section>

        <section className="simple-card logs-card">
          <div className="simple-card-head">
            <div>
              <span className="simple-card-badge">Realtime Logs</span>
              <h2>Deployment logs</h2>
            </div>

            <span>{logs.length} logs</span>
          </div>

          <div className="simple-log-terminal">
            {logs.map((log, index) => (
              <div className="simple-log-line" key={`${log}-${index}`}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <p>{log}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="simple-card logs-card" style={{ marginTop: "28px" }}>
          <div className="simple-card-head">
            <div>
              <span className="simple-card-badge">My Projects</span>
              <h2>Recent deployments</h2>
            </div>

            <span>
              {loadingProjects ? "Loading..." : `${projects.length} projects`}
            </span>
          </div>

          {!hasHostingAccess ? (
            <div className="simple-empty">
              Hosting plan required. Recent deployments will be visible after
              plan activation.
            </div>
          ) : projects.length === 0 ? (
            <div className="simple-empty">
              No deployed projects yet. Upload your first website project.
            </div>
          ) : (
            <div className="simple-file-list">
              {projects.slice(0, 5).map((project) => {
                const projectLiveUrl = getSafeLiveUrl(project);
                const projectPreviewUrl = getSafePreviewUrl(project);
                const projectMappedDomain = getMappedDomain(project);
                const projectDomainStatus = getDomainStatus(project);
                const projectDomainActive = projectDomainStatus === "active";
                const projectSslLabel = getSslLabel(project.sslStatus);

                return (
                  <div className="simple-file-row" key={project.id}>
                    <span>🌐</span>

                    <div>
                      <strong>{project.projectName}</strong>

                      <small>
                        {getStatusLabel(project.status)} • {project.totalFiles}{" "}
                        files • {project.totalSizeLabel}
                      </small>

                      {projectPreviewUrl ? (
                        <small>
                          Preview URL:{" "}
                          <a
                            href={projectPreviewUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {projectPreviewUrl}
                          </a>
                        </small>
                      ) : null}

                      {projectMappedDomain ? (
                        <small>
                          Live URL:{" "}
                          {projectDomainActive && projectLiveUrl ? (
                            <a
                              href={projectLiveUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {projectLiveUrl}
                            </a>
                          ) : (
                            `https://${projectMappedDomain}`
                          )}
                        </small>
                      ) : null}

                      {projectMappedDomain ? (
                        <small>
                          Custom Domain: {projectMappedDomain} •{" "}
                          {getDomainStatusLabel(
                            projectDomainStatus || "pending_dns"
                          )}
                        </small>
                      ) : null}

                      <small>SSL: {projectSslLabel}</small>
                      <small>Email: Not Required</small>

                      {projectMappedDomain && !projectDomainActive ? (
                        <small>
                          Preview URL is live. DNS/SSL is connecting, so user
                          will not see broken status.
                        </small>
                      ) : null}

                      {project.errorMessage ? (
                        <small>{project.errorMessage}</small>
                      ) : null}
                    </div>

                    <button
                      type="button"
                      className="simple-reset-btn"
                      onClick={() => deleteProject(project.id)}
                      style={{
                        width: "auto",
                        minWidth: "110px",
                        marginTop: 0,
                      }}
                    >
                      Delete
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </>
  );
}

export default SimpleCpanel;